import { Button, Form, Input, message, Modal, Select } from 'antd';
import * as React from 'react';
import withStyles from 'react-jss';
import { connect, ConnectedProps } from 'react-redux';
import { withRouter } from 'react-router';
import { bindActionCreators } from 'redux';
import { disableLoadTracking, enableLoadTracking, fetchTrackingData } from 'src/actions/shipmentExecutionActions';
import ApiDto from 'src/dto/ApiDto';
import { addTracking } from 'src/api/shipmentExecution';
import { useGenericState } from 'src/Library/UseGenericState';
import { FormComponentProps } from 'antd/lib/form';
import { StylesProps } from 'src/Library/Types';
import { ExecutionDetails } from '../SEDetails/shipment-details.types';
import { useState } from 'react';
import { interactive } from 'src/design-system/mixins';

const FormItem = Form.Item;

const styles = theme => ({
  label: {
    fontSize: theme.fontSize.small,
    color: theme.colors.text,
    fontWeight: theme.fontWeight.semibold,
    display: 'flex',
    alignItems: 'center',
    height: 36,
  },
  form: {
    display: 'grid',
    gridTemplateColumns: '1fr 4fr',
    '& .ant-form-item': {
      width: '100% !important',
      marginTop: 0,
    },
    ...interactive.antFormRequiredHide,
    '& .ant-form-item-label': {
      lineHeight: '26px',
    },
    '& .ant-form-explain': {
      fontSize: 10,
    },
  },
});

type IProps = ReduxProps & FormComponentProps & StylesProps<ReturnType<typeof styles>> & {
  data: ExecutionDetails;
  isVisible: boolean;
  onClose: () => void;
};

const SEAddAirTrackingModal = (props: IProps) => {
  const {
    isVisible,
    classes,
    form,
    onClose,
    airlineList,
    data,
    enableLoadTracking,
    disableLoadTracking,
  } = props;
  
  const [state, setState] = useGenericState({
    airLineCode: '' as string,
    awbNumber: '' as string,
    airlineSelected: undefined,
  });

  const [isLoading, setIsLoading] = useState(false);

  const { airLineCode, awbNumber, airlineSelected } = state;
  const { getFieldDecorator, getFieldValue, getFieldsError, setFieldsValue } = form;

  const airlineKeyLabel = airlineList.map(item => ({
    key: item.id,
    label: item.name,
  }));

  const handleAirlineCode = (val) => {
    const reg = /^\d*$/;
    if ((!isNaN(val) && reg.test(val)) || val === '') {
      setState({ airLineCode: val });
    }
    setFieldsValue({
      airLine: airlineList.filter((item) => item.id === val)[0]?.id,
    });
  }

  const handleAwbNumber = (val) => {
    const reg = /^\d*$/;
    if ((!isNaN(val) && reg.test(val)) || val === '') {
      setState({ awbNumber: val });
    }
  }

  const airLineCodeInput = () => {
    return (
      <Input
        maxLength={3}
        placeholder={'Code'}
        value={airLineCode}
        onChange={(val) => {
          handleAirlineCode(val.target.value);
        }}
      />
    );
  }

  const awbNumberInput = () => {
    return (
      <Input
        placeholder="Enter 8 digits"
        value={awbNumber}
        onChange={(e) => {
          handleAwbNumber(e.target.value);
        }}
        maxLength={8}
      />
    );
  }

  const getLabel = (label) => {
    return <div className={classes.label}>{label}</div>
  }
 
  const renderAWBNumber = () => {
    return (
      <>
        {getLabel('AWB Number*')}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <FormItem style={{ maxWidth: 75 }}>
            {getFieldDecorator('airLineCode', {
              rules: [
                {
                  required: true,
                  message: 'Required'
                }
              ],
            })(airLineCodeInput())}
          </FormItem>
          <FormItem style={{ marginLeft: 4 }}>
            {getFieldDecorator('awbNumber', {
              rules: [
                {
                  required: true,
                  message: 'Required',
                },
                {
                  min: 8,
                  message: 'Should have atleast 8 characters',
                },
              ],
            })(awbNumberInput())}
          </FormItem>
        </div>
      </>
    );
  };


  const renderAirLineName = () => {
    return (
      <>
        {getLabel('Airline')}
        <FormItem>
          {getFieldDecorator('airLine', {
            rules: [{ required: airLineCode ? true : false, message: "No airline for the following code" }],
          })(<Select disabled={true} placeholder="Airline Name" value={airlineSelected} showArrow={false}>
            {airlineKeyLabel.map(item => (<Select.Option key={item.key}>{item.label}</Select.Option>))}
          </Select>)}
        </FormItem>
      </>
    );
  }

  const renderAddAirShipment = () => {
    return (
      <Form className={classes.form} colon={false} hideRequiredMark>
        {renderAWBNumber()}
        {renderAirLineName()}
      </Form>
    )
  }

  const handleAddTracking = () => {
    form.validateFields(async (err, values) => {
      if (err) {
        return;
      }

      if (!awbNumber?.length) {
        return;
      }
  
      const shipmentId = data.id;
      const movementType = 'EXPORT';
      const executionReferenceNo = data.referenceNumber;
      const formattedAwbNumber = airLineCode + '-' + awbNumber;
  
      const entityNumber = {
        movementType,
        airLine: airLineCode,
        executionReferenceNo,
        shipmentId,
      };
  
      // TODO: add dto
      const awbNumberList = [{ ...entityNumber, awbNumber: formattedAwbNumber }];
  
      setIsLoading(true);
      const response = await addTracking({ shipmentId, awbNumberList });
      if (response.isSuccess) {
        onClose();
        // TODO: need to refactor old actions code
        enableLoadTracking();
        disableLoadTracking();
      } else {
        message.error(response.errorMessage);
      }
      setIsLoading(false);

    });
  }

  return (
    <>
      <Modal title={'Add Tracking'}
        centered
        visible={isVisible} width={400} okText="Submit"
        onCancel={onClose}
        destroyOnClose
        footer={[
          <Button key="back" onClick={onClose}>Cancel</Button>,
          <Button
            loading={isLoading}
            key="confirm"
            type="primary"
            onClick={handleAddTracking}
            disabled={isLoading}
          >Submit</Button>,
        ]}
        zIndex={1060}
        confirmLoading={false}
      >
        <div>
          {renderAddAirShipment()}
        </div>
      </Modal>
    </>
  );
}

const mapDispatchToProps = (dispatch) => (bindActionCreators({
  fetchTrackingData,
  enableLoadTracking,
  disableLoadTracking,
}, dispatch));

const mapStateToProps = ({ masterData }) => ({
  airlineList: masterData.globalAirLines,
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

const SEAddAirTrackingModalStyled = withStyles(styles)(Form.create()(SEAddAirTrackingModal));
export default withRouter(connector(SEAddAirTrackingModalStyled));
